import React, { useCallback, useEffect, useState } from "react";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { Button, Card, Col, Form, ListGroup, Row } from "react-bootstrap";
import { db } from "../../../firebase/FirebaseProvider";
import { ManagedTag } from "../../types/LocationTags";

const sortTags = (tagsToSort: Array<ManagedTag>) => (
    tagsToSort.sort((tagA, tagB) => {
        if (tagA.status !== tagB.status) {
            return tagA.status === "ACTIVE" ? -1 : 1;
        }

        return tagA.name.localeCompare(tagB.name);
    })
);

function AdminLocationTags() {
    const [tags, setTags] = useState<Array<ManagedTag>>([]);
    const [name, setName] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [feedback, setFeedback] = useState<string>("");

    const loadTags = useCallback(async () => {
        setIsLoading(true);

        const tagsQuery = query(
            collection(db, "tags"),
            where("type", "==", "LOCATION")
        );
        const tagsSnapshot = await getDocs(tagsQuery);
        const loadedTags: Array<ManagedTag> = [];

        tagsSnapshot.forEach((tagDoc) => {
            loadedTags.push({
                ...(tagDoc.data() as ManagedTag),
                id: tagDoc.id,
            });
        });

        setTags(sortTags(loadedTags));
        setIsLoading(false);
    }, []);

    useEffect(() => {
        (async () => {
            await loadTags();
        })();
    }, [loadTags]);

    const addTag = async (event: React.FormEvent) => {
        event.preventDefault();
        setFeedback("");

        const trimmedName = name.trim().toLowerCase();
        const trimmedDescription = description.trim();

        if (!trimmedName || !trimmedDescription) {
            setFeedback("Name and description are required.");
            return;
        }

        setIsSaving(true);
        try {
            const newTag: ManagedTag = {
                type: "LOCATION",
                status: "ACTIVE",
                name: trimmedName,
                description: trimmedDescription,
                createdAt: new Date(),
            };

            const tagRef = await addDoc(collection(db, "tags"), newTag);
            setTags((existingTags) => (
                sortTags([...existingTags, { ...newTag, id: tagRef.id }])
            ));
            setName("");
            setDescription("");
            setFeedback("Tag added.");
        } catch (error) {
            console.error("Error adding location tag: ", error);
            setFeedback("Could not add tag.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card className="mb-3">
            <Card.Header>
                <div>
                    <div><b>Location Tags</b></div>
                    <small className="text-muted">Total: {tags.length} tags</small>
                </div>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={addTag} className="mb-3">
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-2" controlId="locationTagName">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Tag name"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={7}>
                            <Form.Group className="mb-2" controlId="locationTagDescription">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Tag description"
                                    value={description}
                                    onChange={(event) => setDescription(event.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <Button className="mb-2 w-100" type="submit" disabled={isSaving}>
                                {isSaving ? "Adding..." : "Add tag"}
                            </Button>
                        </Col>
                    </Row>
                    {feedback && <small className="text-muted">{feedback}</small>}
                </Form>

                {isLoading ? (
                    <div className="text-center p-3">Loading tags...</div>
                ) : tags.length === 0 ? (
                    <div className="text-center p-3">No location tags yet</div>
                ) : (
                    <ListGroup variant="flush" className="admin-location-tags-list">
                        {tags.map((tag) => (
                            <ListGroup.Item key={tag.id || tag.name} className="admin-location-tags-list-item">
                                <div className="d-flex align-items-center">
                                    <div className="location-tag d-inline-block mr-2">{tag.name}</div>
                                    <span>{tag.description}</span>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Card.Body>
        </Card>
    );
}

export default AdminLocationTags;
